// POSIÇÃO e VARIAÇÃO CAMBIAL — fonte única.
//
// Estas funções viviam duplicadas dentro da BudgetPage. O relatório reimplementou
// a mesma ideia "por fora" e esqueceu a variação cambial: as duas telas passaram a
// mostrar números diferentes para o MESMO mês. Ter uma cópia só é o que impede
// isso de acontecer de novo.

import { convertWithRates, amountInAccount } from '@/hooks/useCurrentRates'
import { faturaEndISO, faturaMonthOf, type Rates } from '@/lib/finance'
import type { Account } from '@/types/db'

// Só interessam os campos que a conta/saldo usa.
export interface PosTx {
  account_id: string
  date: string
  kind: string
  status: string
  amount: number
  currency: string
  amount_base: number | null
  amount_account: number | null
  transfer_group_id: string | null
  statement_month?: string | null
}

// Um lançamento entra no SALDO (e é avaliado pela taxa de hoje) se a conta for
// CARTÃO (compra lançada já é dívida real) ou se estiver PAGO. Um pendente fora
// de cartão o dashboard soma pela base congelada, igual ao orçamento — entra dos
// dois lados e se cancela, então não tem efeito cambial.
function inBalance(a: Account, status: string) {
  return a.type === 'cartao' || status === 'paid'
}

// POSIÇÃO REAL (contas + cartões) no fim de `endISO`, avaliada pela taxa de HOJE.
// Espelha exatamente o saldo do dashboard. Cartão entra COM SINAL: negativo =
// dívida, positivo = crédito a favor.
//
// O recorte é pela DATA DE CAIXA (`faturaEndISO`), não pela data da compra: uma
// compra de cartão feita depois do fechamento só tira dinheiro na fatura seguinte,
// então ela não pode derrubar a posição do mês em que foi feita. Sem fatura
// marcada isso é a própria data — identidade para todo o histórico importado.
//
// A conversão é UMA vez por conta — `convertWithRates` arredonda a cada chamada,
// então converter transação a transação acumularia centavos de diferença.
export function posAt(endISO: string, txs: PosTx[], accounts: Account[], rates: Rates): number {
  const live = accounts.filter((a) => !a.archived)
  const byId = new Map(live.map((a) => [a.id, a]))
  const bal = new Map<string, number>()
  for (const a of live) bal.set(a.id, Number(a.initial_balance))

  for (const t of txs) {
    if (faturaEndISO(t) > endISO) continue
    const a = byId.get(t.account_id)
    if (!a || !inBalance(a, t.status)) continue
    const v = amountInAccount(t, a.currency, rates)
    bal.set(a.id, (bal.get(a.id) ?? 0) + (t.kind === 'income' ? v : t.kind === 'expense' ? -v : 0))
  }

  let total = 0
  for (const a of live) total += convertWithRates(bal.get(a.id) ?? 0, a.currency, rates) ?? 0
  return total
}

// Movimentação de um conjunto de lançamentos em BASE CONGELADA — o que o
// orçamento soma. Só os que também estão no saldo; os demais se cancelam.
export function baseFlowOf(txs: PosTx[], accounts: Account[]): number {
  const byId = new Map(accounts.map((a) => [a.id, a]))
  let base = 0
  for (const t of txs) {
    if ((t.kind !== 'income' && t.kind !== 'expense') || t.amount_base == null) continue
    if (t.transfer_group_id) continue
    const a = byId.get(t.account_id)
    if (!a || !inBalance(a, t.status)) continue
    base += (t.kind === 'income' ? 1 : -1) * Number(t.amount_base)
  }
  return base
}

function iso(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// VARIAÇÃO CAMBIAL de um mês = quanto a POSIÇÃO (taxa de hoje) se moveu, menos a
// movimentação em BASE CONGELADA. Você guarda dinheiro em BRL/USD, cujo valor em
// GTQ muda com a cotação — essa é a diferença entre as duas réguas.
//
// Definida por DIFERENÇA DE POSIÇÕES (não item a item) para casar o arredondamento
// com o dashboard, centavo a centavo.
//
// `txs` precisa ser o histórico COMPLETO até o fim do mês — o posAt reconstrói o
// saldo desde o saldo inicial da conta.
export function fxOfMonth(y: number, m0: number, txs: PosTx[], accounts: Account[], rates: Rates): number {
  const endISO = iso(new Date(y, m0 + 1, 0))
  const prevEndISO = iso(new Date(y, m0, 0))
  const mk = `${y}-${String(m0 + 1).padStart(2, '0')}`
  // mesma régua do posAt: o mês de CAIXA, não o da compra
  const mTxs = txs.filter((t) => faturaMonthOf(t) === mk)
  return posAt(endISO, txs, accounts, rates) - posAt(prevEndISO, txs, accounts, rates) - baseFlowOf(mTxs, accounts)
}

// Parte da variação do mês que vem das TRANSFERÊNCIAS entre moedas: o que saiu
// menos o que chegou, ambos pela taxa de hoje — o spread/tarifa do câmbio, custo
// que de fato aconteceu. O resto de `fxOfMonth` é só reavaliação de lançamentos
// pela cotação de hoje vs a congelada (ruído: não é dinheiro realizado).
export function transferFxOfMonth(y: number, m0: number, txs: PosTx[], accounts: Account[], rates: Rates): number {
  const mk = `${y}-${String(m0 + 1).padStart(2, '0')}`
  const byId = new Map(accounts.filter((a) => !a.archived).map((a) => [a.id, a]))
  let total = 0
  for (const t of txs) {
    if (!t.transfer_group_id || faturaMonthOf(t) !== mk) continue
    if (t.kind !== 'income' && t.kind !== 'expense') continue
    const a = byId.get(t.account_id)
    if (!a || !inBalance(a, t.status)) continue
    const v = convertWithRates(amountInAccount(t, a.currency, rates), a.currency, rates) ?? 0
    total += t.kind === 'income' ? v : -v
  }
  return Math.round(total * 100) / 100
}
